import { apiClient } from './apiClient';
import { 
  Invoice, 
  CreateInvoiceRequest, 
  UpdateInvoiceRequest, 
  ChangeStatusRequest,
  GenerateNumberResponse 
} from '../types/invoice';
import { Language } from '../types/language';

export const invoiceService = {
  // Получить список инвойсов компании
  getCompanyInvoices: async (companyId: string): Promise<Invoice[]> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices`);
    return response.data;
  },

  // Получить инвойс по ID
  getInvoice: async (companyId: string, invoiceId: string): Promise<Invoice> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices/${invoiceId}`);
    return response.data;
  },

  // Проверить существование инвойса по номеру
  checkInvoiceExists: async (companyId: string, invoiceNumber: string): Promise<boolean> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices/check?invoiceNumber=${encodeURIComponent(invoiceNumber)}`);
    return response.data;
  },

  // Создать новый инвойс
  createInvoice: async (companyId: string, data: CreateInvoiceRequest): Promise<Invoice> => {
    const response = await apiClient.post(`/companies/${companyId}/invoices`, data);
    return response.data;
  },

  // Обновить инвойс
  updateInvoice: async (companyId: string, invoiceId: string, data: UpdateInvoiceRequest): Promise<Invoice> => {
    const response = await apiClient.put(`/companies/${companyId}/invoices/${invoiceId}`, data);
    return response.data;
  },

  // Удалить инвойс
  deleteInvoice: async (companyId: string, invoiceId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/invoices/${invoiceId}`);
  },

  // Изменить статус инвойса
  changeInvoiceStatus: async (companyId: string, invoiceId: string, status: ChangeStatusRequest): Promise<Invoice> => {
    const response = await apiClient.put(`/companies/${companyId}/invoices/${invoiceId}/status`, status);
    return response.data;
  },

  // Обновить статус инвойса (упрощенный метод)
  updateInvoiceStatus: async (
    invoiceId: string,
    newStatus: string,
    forceResync?: boolean,
    correction?: { correctedTotalAmount: number; correctedCurrency: string }
  ): Promise<Invoice> => {
    // Извлекаем companyId из URL или используем другой способ получения
    const companyId = window.location.pathname.split('/')[2]; // Предполагаем URL вида /companies/{companyId}
    const response = await apiClient.put(`/companies/${companyId}/invoices/${invoiceId}/status`, {
      status: newStatus,
      forceResync,
      correctedTotalAmount: correction?.correctedTotalAmount,
      correctedCurrency: correction?.correctedCurrency,
    });
    return response.data;
  },

  // Генерировать номер инвойса (инкрементирует счетчик - использовать только при создании)
  generateInvoiceNumber: async (companyId: string, issueDate?: string): Promise<GenerateNumberResponse> => {
    const params = issueDate ? `?issueDate=${issueDate}` : '';
    const response = await apiClient.get(`/companies/${companyId}/invoices/generate-number${params}`);
    return response.data;
  },

  // Предварительный просмотр номера инвойса (не инкрементирует счетчик)
  previewInvoiceNumber: async (companyId: string, issueDate?: string): Promise<GenerateNumberResponse> => {
    const params = issueDate ? `?issueDate=${issueDate}` : '';
    const response = await apiClient.get(`/companies/${companyId}/invoices/preview-number${params}`);
    return response.data;
  },

  // Поиск инвойсов
  searchInvoices: async (companyId: string, searchTerm: string): Promise<Invoice[]> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  // Скачать PDF инвойса
  downloadInvoicePdf: async (companyId: string, invoiceId: string, invoiceNumber: string, clientName: string, language: Language = Language.PL_ONLY): Promise<void> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices/${invoiceId}/pdf?language=${language}`, {
      responseType: 'blob'
    });
    
    // Создаем ссылку для скачивания
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    // Очищаем имя клиента от недопустимых символов для имени файла
    const cleanClientName = clientName.replace(/[^a-zA-Z0-9а-яА-Я\s-]/g, '').replace(/\s+/g, '-');
    // Формируем имя файла: {номер}-{имя клиента}-{pl/plen/en}
    const langSuffix = language === Language.PL_ONLY ? 'pl' : language === Language.PL_EN ? 'plen' : 'en';
    link.setAttribute('download', `${invoiceNumber}-${cleanClientName}-${langSuffix}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Получить инвойсы компании с пагинацией
  getCompanyInvoicesPaginated: async (companyId: string, params: URLSearchParams): Promise<any> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices/paginated?${params}`);
    return response.data;
  },

  // Получить инвойсы контрагента с пагинацией
  getCounterpartyInvoicesPaginated: async (companyId: string, counterpartyId: string, params: URLSearchParams): Promise<any> => {
    const response = await apiClient.get(`/companies/${companyId}/invoices/counterparty/${counterpartyId}/paginated?${params}`);
    return response.data;
  },

  /** Excel-отчёт по фактурам за календарный месяц (ISSUE | SALE) */
  downloadMonthlyInvoiceReport: async (
    companyId: string,
    year: number,
    month: number,
    dateBasis: 'ISSUE' | 'SALE' = 'ISSUE'
  ): Promise<void> => {
    const response = await apiClient.get(
      `/companies/${companyId}/invoices/monthly-report.xlsx?year=${year}&month=${month}&dateBasis=${dateBasis}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `faktury-raport-${year}-${String(month).padStart(2, '0')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /** Excel VAT report for month by sale date (SENT only) */
  downloadMonthlyVatReport: async (companyId: string, year: number, month: number): Promise<void> => {
    const response = await apiClient.get(
      `/companies/${companyId}/invoices/monthly-vat-report.xlsx?year=${year}&month=${month}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `faktury-vat-raport-${year}-${String(month).padStart(2, '0')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /** Пересчёт totalAmount, taxAmount, taxAmountInPln в БД за период (как в PDF). refreshExchangeRates — подтянуть курс NBP */
  recalculateStoredTotals: async (
    companyId: string,
    startDate: string,
    endDate: string,
    refreshExchangeRates = false
  ): Promise<{
    totalInvoices: number;
    completedOk: number;
    failed: number;
    invoicesWithModifiedAmounts: number;
    errorMessages: string[];
  }> => {
    const response = await apiClient.post(`/companies/${companyId}/invoices/recalculate-stored-totals`, {
      startDate,
      endDate,
      refreshExchangeRates,
    });
    return response.data;
  },
};
